import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Play, Pause, RotateCcw } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }
interface P { x: number; y: number; vx: number; vy: number }

// Mô hình động học phân tử chất khí (Lớp 12 — Bài 11/7):
// phân tử chuyển động hỗn loạn, va chạm thành bình → áp suất; nhiệt độ ↑ → tốc độ ↑.
const GasParticleSimulation: React.FC<Props> = ({
  title = 'Mô hình động học phân tử chất khí',
  description = 'Phân tử khí chuyển động hỗn loạn và va chạm vào thành bình tạo áp suất. Tăng nhiệt độ → động năng & tốc độ tăng.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const ps = useRef<P[]>([])
  const hitsRef = useRef(0)
  const [running, setRunning] = useState(true)
  const [temp, setTemp] = useState(5)   // nhiệt độ tương đối 1..10
  const [count, setCount] = useState(60)
  const [pressure, setPressure] = useState(0)
  const firstRef = useRef(false)
  const W = 460, H = 300

  const speedForTemp = (t: number) => 0.6 + t * 0.5
  const build = useCallback((n: number, t: number) => {
    const sp = speedForTemp(t)
    ps.current = Array.from({ length: n }, () => {
      const a = Math.random() * Math.PI * 2
      return { x: 20 + Math.random() * (W - 40), y: 20 + Math.random() * (H - 40), vx: Math.cos(a) * sp, vy: Math.sin(a) * sp }
    })
  }, [])

  const reset = useCallback(() => { build(count, temp); hitsRef.current = 0; setPressure(0) }, [count, temp, build])
  useEffect(() => { build(count, temp) }, [count]) // eslint-disable-line react-hooks/exhaustive-deps

  // đổi nhiệt độ → đổi tốc độ (giữ hướng)
  useEffect(() => {
    const sp = speedForTemp(temp)
    ps.current.forEach((p) => {
      const m = Math.hypot(p.vx, p.vy) || 1
      p.vx = (p.vx / m) * sp; p.vy = (p.vy / m) * sp
    })
  }, [temp])

  const tick = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 2; ctx.strokeRect(6, 6, W - 12, H - 12)
    let hits = 0
    for (const p of ps.current) {
      p.x += p.vx; p.y += p.vy
      if (p.x < 12) { p.x = 12; p.vx = Math.abs(p.vx); hits++ }
      if (p.x > W - 12) { p.x = W - 12; p.vx = -Math.abs(p.vx); hits++ }
      if (p.y < 12) { p.y = 12; p.vy = Math.abs(p.vy); hits++ }
      if (p.y > H - 12) { p.y = H - 12; p.vy = -Math.abs(p.vy); hits++ }
      ctx.beginPath(); ctx.arc(p.x, p.y, 4, 0, Math.PI * 2); ctx.fillStyle = '#3b82f6'; ctx.fill()
    }
    hitsRef.current = hitsRef.current * 0.9 + hits * 0.1
    setPressure(Math.round(hitsRef.current * 20))
    raf.current = requestAnimationFrame(tick)
  }, [])

  useEffect(() => {
    if (running) { if (!firstRef.current) { firstRef.current = true; onComplete?.() } raf.current = requestAnimationFrame(tick) }
    else if (raf.current) cancelAnimationFrame(raf.current)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [running, tick, onComplete])

  return (
    <div className="rounded-2xl border border-blue-200 bg-white p-4 my-3">
      <h4 className="font-bold text-blue-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <div className="grid grid-cols-2 gap-2 my-3 text-center">
        <div className="p-2 rounded-lg bg-rose-50"><div className="text-lg font-black text-rose-700">{pressure}</div><div className="text-[11px] text-gray-500">Áp suất (tương đối)</div></div>
        <div className="p-2 rounded-lg bg-amber-50"><div className="text-lg font-black text-amber-700">{(temp * 30).toFixed(0)} K</div><div className="text-[11px] text-gray-500">Động năng TB ~ T</div></div>
      </div>
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm"><span className="w-28 text-gray-600">Nhiệt độ</span>
          <input type="range" min={1} max={10} value={temp} onChange={(e) => setTemp(Number(e.target.value))} className="flex-1" />
          <span className="w-10 text-right font-semibold">{temp}</span></label>
        <label className="flex items-center gap-2 text-sm"><span className="w-28 text-gray-600">Số phân tử</span>
          <input type="range" min={20} max={150} step={10} value={count} onChange={(e) => setCount(Number(e.target.value))} className="flex-1" />
          <span className="w-10 text-right font-semibold">{count}</span></label>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => setRunning((r) => !r)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700">
          {running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
        <button onClick={reset} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50">
          <RotateCcw className="w-4 h-4" /> Đặt lại</button>
      </div>
    </div>
  )
}
export default GasParticleSimulation
